import { useState } from "react";
import { Send, Eye, CheckCircle2, XCircle, Undo2, Archive, RotateCcw, Trash2, UserPlus, Pencil } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ActionDialog } from "./ActionDialog";

const CFG = {
  submit: { title: "Submit for approval", description: "Required fields and attachments are checked before routing.", approver: true, cta: "Submit" },
  assign: { title: "Assign approver", approver: true, cta: "Assign" },
  approve: { title: "Approve record", description: "Approved records become locked. Further edits require a reopen.", comment: true, cta: "Approve" },
  reject: { title: "Reject record", comment: true, commentRequired: true, commentLabel: "Reason for rejection", cta: "Reject", danger: true },
  return: { title: "Return for correction", comment: true, commentRequired: true, commentLabel: "What needs correcting?", cta: "Return" },
  archive: { title: "Archive record", description: "Archived records stay retrievable by permission.", comment: true, commentLabel: "Note (optional)", cta: "Archive" },
  reopen: { title: "Reopen record", description: "Creates a new revision. The previous approved version is kept in history.", comment: true, commentRequired: true, commentLabel: "Reason for reopening", cta: "Reopen" },
  delete: { title: "Permanently delete record", description: "This cannot be undone. The deletion is written to the immutable audit log.", comment: true, commentRequired: true, commentLabel: "Reason for deletion", confirmRef: true, cta: "Delete permanently", danger: true },
};

export const WorkflowBar = ({ record, actions, onDone, onEdit, onDeleted }) => {
  const [open, setOpen] = useState(null);
  const id = record.record_id;
  const run = async (key, v) => {
    try {
      if (key === "submit") await api.post(`/records/${id}/submit`, { assigned_to: v.approver });
      if (key === "assign") await api.post(`/records/${id}/assign`, { assigned_to: v.approver });
      if (["approve", "reject", "return"].includes(key)) await api.post(`/records/${id}/decision`, { decision: key, comment: v.comment });
      if (key === "archive") await api.post(`/records/${id}/archive`, { reason: v.comment });
      if (key === "reopen") await api.post(`/records/${id}/reopen`, { reason: v.comment });
      if (key === "delete") { await api.delete(`/records/${id}`, { data: { reason: v.comment, confirm_ref: v.confirm } }); toast.success("Record permanently deleted"); setOpen(null); onDeleted(); return; }
      toast.success(`${CFG[key].cta} — done`);
      setOpen(null);
      onDone();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const review = async () => { try { await api.post(`/records/${id}/review`); toast.success("Review started"); onDone(); } catch (e) { toast.error(errMsg(e)); } };
  const B = ({ k, icon: I, label, variant = "outline", cls = "", onClick }) => (
    <Button size="sm" variant={variant} className={cls} onClick={onClick || (() => setOpen(k))} data-testid={`${k}-action-button`}><I className="w-4 h-4 mr-1.5" />{label}</Button>
  );
  return (
    <div className="flex flex-wrap gap-2" data-testid="workflow-action-bar">
      {actions.edit && <B k="edit" icon={Pencil} label="Edit" onClick={onEdit} />}
      {actions.submit && <B k="submit" icon={Send} label="Submit" variant="default" />}
      {actions.assign && !actions.submit && <B k="assign" icon={UserPlus} label="Assign" />}
      {actions.start_review && <B k="review" icon={Eye} label="Start review" onClick={review} />}
      {actions.decide && <>
        <B k="approve" icon={CheckCircle2} label="Approve" variant="default" cls="bg-emerald-600 hover:bg-emerald-700" />
        <B k="return" icon={Undo2} label="Return" />
        <B k="reject" icon={XCircle} label="Reject" cls="text-rose-700 border-rose-200 hover:bg-rose-50" />
      </>}
      {actions.archive && <B k="archive" icon={Archive} label="Archive" />}
      {actions.reopen && <B k="reopen" icon={RotateCcw} label="Reopen" />}
      {actions.hard_delete && <Button size="sm" variant="ghost" className="text-rose-700 hover:bg-rose-50" onClick={() => setOpen("delete")} data-testid="super-admin-hard-delete-button"><Trash2 className="w-4 h-4 mr-1.5" />Hard delete</Button>}
      {open && <ActionDialog key={open} cfg={CFG[open]} record={record} onClose={() => setOpen(null)} onConfirm={(v) => run(open, v)} />}
    </div>
  );
};
